import { motion } from "framer-motion";
import React from "react";

import { classed } from "@/components/classed.config";
import cn from "@/helpers/classNames";
import { useCurrentAccount } from "@/providers/account";
import { useMatchRoute } from "@tanstack/react-location";
import { Link } from "@tanstack/react-location";

import { ReverseEltJobsCompletionStats } from "./modules/RevEltJobsCompletionStats";

function useTabs() {
  const account = useCurrentAccount();
  return React.useMemo(() => {
    const basePath = `/${account.slug}/reverse-etl`;
    return [
      {
        name: "Syncs",
        href: basePath,
        id: "syncs",
      },
      {
        name: "Usage Statistics",
        href: `${basePath}/usage`,
        id: "usage",
      },
    ];
  }, [account]);
}

const Content = classed.div(
  "mx-auto w-full max-w-8xl grow px-4 sm:px-6 md:px-8",
);

export default function MainTabsLayout(props: React.PropsWithChildren<{}>) {
  const tabs = useTabs();

  const matchRoute = useMatchRoute();

  function isSelected(currentPath: string | undefined) {
    if (currentPath === undefined) return false;
    return matchRoute({ to: currentPath });
  }

  return (
    <main className="relative z-0 flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
      <header className="sticky top-0 z-10 shrink-0 items-center gap-2 border-b bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60 dark:bg-gray-900/95 dark:supports-[backdrop-filter]:bg-gray-900/60">
        <Content className="flex">
          <div>
            <div className="flex h-12 items-center">Reverse ETL</div>
            <nav className="relative mt-4 flex">
              {tabs.map((tab) => (
                <Link
                  key={tab.name}
                  to={tab.href}
                  className={cn(
                    "relative cursor-pointer whitespace-nowrap px-5 pb-3 text-sm font-medium leading-none text-muted-foreground hover:text-blue-500",
                    {
                      "cursor-default text-blue-500": isSelected(tab.href),
                    },
                  )}
                >
                  {tab.name}
                  {isSelected(tab.href) && (
                    <motion.div
                      className="absolute -bottom-px left-0 right-0 h-[2px] rounded-full bg-blue-500"
                      layoutId="sub-page-tabs"
                    />
                  )}
                </Link>
              ))}
            </nav>
          </div>
          <div className="ml-auto hidden items-end sm:flex">
            <ReverseEltJobsCompletionStats />
          </div>
        </Content>
      </header>

      <Content className="flex flex-col py-8">{props.children}</Content>
    </main>
  );
}
